import { AnnouncementPreviewItemModel } from "client/Api/Hub/Announcements/announcementViewer";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { ReactElement, useMemo } from "react";
import { HubItemStatistic, ListItemProps } from "../common";
import { ListItemBase } from "./ListItemBase";
import { useHubStatisticsFormatter } from "client/Features/Hub/useHubStatisticsFormatter";
import { NumberFormatter } from "client/Hooks";
import { isNotNil } from "client/Misc/utils";
import { ReactionStatistic, useReactionStatistic } from "client/Features/Reactions/useReactionStatistic";

export type AnnouncementListItemProps = ListItemProps<AnnouncementPreviewItemModel>;

export const AnnouncementListItem = (props: AnnouncementListItemProps): ReactElement => {
	const {
		item,
		isWideTile,
		isUltraWideTile,
		onOpen,
		timezone
	} = props;

	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();
	const reactionStatistic: ReactionStatistic = useReactionStatistic(item.reactionCounters);

	const statistics = useMemo(
		(): readonly HubItemStatistic[] => {
			const result: HubItemStatistic[] = [];

			result.push({
				icon: reactionStatistic.icon,
				text: statisticsFormatter(reactionStatistic.total),
				trackingId: "reaction-count"
			});

			if (item.areCommentsEnabled) {
				result.push({
					icon: "comment",
					text: statisticsFormatter(item.commentsCount),
					trackingId: "comment-count"
				});
			}

			return result;
		},
		[
			item.areCommentsEnabled,
			item.commentsCount,
			reactionStatistic.icon,
			reactionStatistic.total,
			statisticsFormatter
		]
	);

	const categoryNames = useMemo(
		(): readonly string[] => {
			return item.categories
				.map(x => x.name);
		},
		[item.categories]
	);

	return (
		<ListItemBase
			itemKind={HubItemKind.Announcement}
			id={item.id}
			title={item.title}
			media={item.media}
			startDate={item.publicationStart}
			statistics={statistics}
			categoryNames={categoryNames}
			descriptionPreview={item.descriptionPreview}
			isWideTile={isWideTile}
			trackingIdBase="announcement"
			onOpen={onOpen}
			authorName={item.authorName}
			isUltraWideTile={isUltraWideTile}
			timezone={timezone}
			isPinned={isNotNil(item.pinDate)}
			isNew={item.isNew}
		/>
	);
};
AnnouncementListItem.displayName = "AnnouncementListItem";
